import { apiService } from "@/services/api";

/**
 * Store para gestionar empleados del tambo
 * - Crear/listar empleados
 * - Gestionar áreas
 * - Gestionar sueldos
 */
export default {
  namespaced: true,

  state: {
    empleados: [],
    areas: [],
    sueldos: [],
    resumenPagos: null,
    loading: false,
    error: null,
  },

  mutations: {
    setEmpleados(state, empleados) {
      state.empleados = empleados;
    },
    addEmpleado(state, empleado) {
      state.empleados.push(empleado);
    },
    upsertEmpleado(state, empleado) {
      const index = state.empleados.findIndex((e) => e.id === empleado.id);
      if (index === -1) {
        state.empleados.push(empleado);
      } else {
        state.empleados.splice(index, 1, empleado);
      }
    },
    setAreas(state, areas) {
      state.areas = areas;
    },
    addArea(state, area) {
      state.areas.push(area);
    },
    setSueldos(state, sueldos) {
      state.sueldos = sueldos;
    },
    addSueldo(state, sueldo) {
      state.sueldos.push(sueldo);
    },
    setResumenPagos(state, resumen) {
      state.resumenPagos = resumen;
    },
    setLoading(state, loading) {
      state.loading = loading;
    },
    setError(state, error) {
      state.error = error;
    },
    clearError(state) {
      state.error = null;
    },
  },

  getters: {
    empleados: (state) => state.empleados,
    areas: (state) => state.areas,
    sueldos: (state) => state.sueldos,
    resumenPagos: (state) => state.resumenPagos,
    loading: (state) => state.loading,
    error: (state) => state.error,

    // Obtener empleado por user_id
    getEmpleadoByUserId: (state) => (userId) => {
      return state.empleados.find((e) => e.user_id === userId);
    },

    // Verificar si un usuario tiene perfil de empleado
    tienePerfilEmpleado: (state) => (userId) => {
      return state.empleados.some((e) => e.user_id === userId);
    },

    // Obtener área por ID
    getAreaById: (state) => (areaId) => {
      return state.areas.find((a) => a.id === areaId);
    },

    // Sueldos de un empleado
    getSueldosByEmpleadoId: (state) => (empleadoId) => {
      return state.sueldos.filter((s) => s.empleado_id === empleadoId);
    },
  },

  actions: {
    // =============================================
    // EMPLEADOS
    // =============================================

    async getEmpleados({ commit }) {
      commit("setLoading", true);
      commit("clearError");

      try {
        const response = await apiService.getCustom("empleados/");
        if (response && response.data) {
          commit("setEmpleados", response.data);
        }
        return response;
      } catch (error) {
        console.error("Error obteniendo empleados:", error);
        commit(
          "setError",
          error.response?.data?.detail || "Error al obtener empleados"
        );
        throw error;
      } finally {
        commit("setLoading", false);
      }
    },

    async crearEmpleado({ commit, dispatch }, empleadoData) {
      commit("setLoading", true);
      commit("clearError");

      try {
        const response = await apiService.postCustom(
          "empleados/",
          empleadoData
        );
        if (response && response.data) {
          const empleadoCreado = response.data;
          // Recargar lista para asegurar consistencia
          await dispatch("getEmpleados");
          commit("upsertEmpleado", empleadoCreado);
        }
        return response;
      } catch (error) {
        console.error("Error creando empleado:", error);
        commit(
          "setError",
          error.response?.data?.detail || "Error al crear empleado"
        );
        throw error;
      } finally {
        commit("setLoading", false);
      }
    },

    // =============================================
    // ÁREAS
    // =============================================

    async getAreas({ commit }) {
      try {
        const response = await apiService.getCustom("empleados/areas/");
        if (response && response.data) {
          commit("setAreas", response.data);
        }
        return response;
      } catch (error) {
        console.error("Error obteniendo áreas:", error);
        throw error;
      }
    },

    async crearArea({ commit }, areaData) {
      commit("clearError");

      try {
        const response = await apiService.postCustom(
          "empleados/areas/",
          areaData
        );
        if (response && response.data) {
          commit("addArea", response.data);
        }
        return response;
      } catch (error) {
        console.error("Error creando área:", error);
        commit(
          "setError",
          error.response?.data?.detail || "Error al crear área"
        );
        throw error;
      }
    },

    // =============================================
    // SUELDOS
    // =============================================

    async getSueldos({ commit }) {
      try {
        const response = await apiService.getCustom("empleados/sueldos/");
        if (response && response.data) {
          commit("setSueldos", response.data);
        }
        return response;
      } catch (error) {
        console.error("Error obteniendo sueldos:", error);
        throw error;
      }
    },

    async cargarSueldo({ commit, dispatch }, sueldoData) {
      commit("clearError");

      try {
        const response = await apiService.postCustom(
          "empleados/sueldos/",
          sueldoData
        );
        if (response && response.data) {
          commit("addSueldo", response.data);
          await dispatch("getSueldos");
        }
        return response;
      } catch (error) {
        console.error("Error cargando sueldo:", error);
        commit(
          "setError",
          error.response?.data?.detail || "Error al cargar sueldo"
        );
        throw error;
      }
    },

    // =============================================
    // RESUMEN DE PAGOS
    // =============================================

    async getResumenPagos(
      { commit },
      { empleadoId, fecha_desde, fecha_hasta }
    ) {
      commit("setLoading", true);
      commit("clearError");

      try {
        const params = {};
        if (fecha_desde) params.fecha_desde = fecha_desde;
        if (fecha_hasta) params.fecha_hasta = fecha_hasta;

        const response = await apiService.getCustom(
          `empleados/${empleadoId}/resumen-pagos`,
          { params }
        );

        if (response && response.data) {
          commit("setResumenPagos", response.data);
        }
        return response;
      } catch (error) {
        console.error("Error obteniendo resumen de pagos:", error);
        commit(
          "setError",
          error.response?.data?.detail || "Error al obtener resumen de pagos"
        );
        throw error;
      } finally {
        commit("setLoading", false);
      }
    },

    clearResumenPagos({ commit }) {
      commit("setResumenPagos", null);
    },

    // =============================================
    // HISTORIAL REPRODUCTIVO
    // =============================================

    async getHistorialReproductivo(
      { commit },
      { empleadoId, tipo = "todos", limit = 100 }
    ) {
      commit("clearError");

      try {
        const response = await apiService.getCustom(
          `empleados/${empleadoId}/historial-reproductivo`,
          {
            params: {
              tipo,
              limit,
            },
          }
        );
        return response;
      } catch (error) {
        console.error("Error obteniendo historial reproductivo:", error);
        commit(
          "setError",
          error.response?.data?.detail ||
            "Error al obtener historial reproductivo"
        );
        throw error;
      }
    },

    // =============================================
    // VINCULAR / DESVINCULAR CUENTA
    // =============================================

    async vincularCuenta({ commit, dispatch }, { empleadoId, userId }) {
      commit("setLoading", true);
      commit("clearError");

      try {
        const response = await apiService.patchCustom(
          `empleados/${empleadoId}/vincular-cuenta?user_id=${userId}`
        );
        await dispatch("getEmpleados");
        return response;
      } catch (error) {
        console.error("Error vinculando cuenta:", error);
        commit(
          "setError",
          error.response?.data?.detail || "Error al vincular cuenta"
        );
        throw error;
      } finally {
        commit("setLoading", false);
      }
    },

    async desvincularCuenta({ commit, dispatch }, empleadoId) {
      commit("setLoading", true);
      commit("clearError");

      try {
        const response = await apiService.patchCustom(
          `empleados/${empleadoId}/desvincular-cuenta`
        );
        await dispatch("getEmpleados");
        return response;
      } catch (error) {
        console.error("Error desvinculando cuenta:", error);
        commit(
          "setError",
          error.response?.data?.detail || "Error al desvincular cuenta"
        );
        throw error;
      } finally {
        commit("setLoading", false);
      }
    },
  },
};
